import { HubItemStatisticKind } from "client/Api/Analytics/analyticsStatistics";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { InlineOptionSelectorItem } from "client/Components";
import { NumberFormatter, useNumberFormatter } from "client/Hooks";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { useCallback } from "react";

const MAX_ANALYTICS_NUMBER_VALUE = 999_999_999_999_999;

export type StatisticsFormatter = (value: number, shouldFormatWithoutMaxValueLimit?: boolean) => string;

export function useStatisticsFormatters(): StatisticsFormatter {
	const formatter: NumberFormatter = useNumberFormatter({
		culture: currentCulture,
		format: "thousand-separator",
		maxValue: MAX_ANALYTICS_NUMBER_VALUE
	});

	const formatterWithoutMaxValue: NumberFormatter = useNumberFormatter({
		culture: currentCulture,
		format: "thousand-separator"
	});

	return useCallback(
		(value: number, shouldFormatWithoutMaxValueLimit: boolean = false): string => {
			if (shouldFormatWithoutMaxValueLimit) {
				return (formatterWithoutMaxValue(value));
			}

			return formatter(value);
		},
		[formatter, formatterWithoutMaxValue]
	);
}

export const hubItemKindPerStatisticsKind: { [key in HubItemStatisticKind]: HubItemKind } = {
	[HubItemStatisticKind.AnnouncementComments]: HubItemKind.Announcement,
	[HubItemStatisticKind.AnnouncementReactions]: HubItemKind.Announcement,
	[HubItemStatisticKind.AnnouncementUniqueViews]: HubItemKind.Announcement,
	[HubItemStatisticKind.EventAttendance]: HubItemKind.Event,
	[HubItemStatisticKind.EventUniqueViews]: HubItemKind.Event,
	[HubItemStatisticKind.EventComments]: HubItemKind.Event,
	[HubItemStatisticKind.EventReactions]: HubItemKind.Event,
	[HubItemStatisticKind.KnowledgeItemTotalViews]: HubItemKind.KnowledgeItem,
	[HubItemStatisticKind.KnowledgeItemUniqueViews]: HubItemKind.KnowledgeItem,
	[HubItemStatisticKind.KnowledgeItemComments]: HubItemKind.KnowledgeItem,
	[HubItemStatisticKind.KnowledgeItemReactions]: HubItemKind.KnowledgeItem
};

export const errorMessageKeyPerHubStatisticsKind: { [key in HubItemStatisticKind]: LocalizationKey; } = {
	[HubItemStatisticKind.AnnouncementUniqueViews]: "_WeCannotRetrieveTheDataForUniqueViews",
	[HubItemStatisticKind.AnnouncementComments]: "_WeCannotRetrieveTheDataForComments",
	[HubItemStatisticKind.AnnouncementReactions]: "_WeCannotRetrieveTheDataForReactions",
	[HubItemStatisticKind.EventAttendance]: "_WeCannotRetrieveTheDataForSignUps",
	[HubItemStatisticKind.EventUniqueViews]: "_WeCannotRetrieveTheDataForUniqueViews",
	[HubItemStatisticKind.EventComments]: "_WeCannotRetrieveTheDataForComments",
	[HubItemStatisticKind.EventReactions]: "_WeCannotRetrieveTheDataForReactions",
	[HubItemStatisticKind.KnowledgeItemUniqueViews]: "_WeCannotRetrieveTheDataForUniqueViews",
	[HubItemStatisticKind.KnowledgeItemTotalViews]: "_WeCannotRetrieveTheDataForTotalViews",
	[HubItemStatisticKind.KnowledgeItemComments]: "_WeCannotRetrieveTheDataForComments",
	[HubItemStatisticKind.KnowledgeItemReactions]: "_WeCannotRetrieveTheDataForReactions"
};

export const daysRangeOptions: readonly InlineOptionSelectorItem<number>[] = [
	{ text: localize("_last24Hours"), value: 1 },
	{ text: localize("_last48Hours"), value: 2 },
	{ text: localize("_last7Days"), value: 7 },
	{ text: localize("_last30Days"), value: 30 },
	{ text: localize("_last60Days"), value: 60 },
];

const currentCulture: string = getCurrentCulture();
